import React, { useState, useEffect } from 'react';
import { Box, Modal, IconButton, Typography, Button } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { resolvePledgePhotoUrl } from '../../utils/pledgePhotoUrl';

/** Small thumbnail that opens a zoom modal with close button. */
export default function PhotoThumb({
  src,
  label = 'Photo',
  size = 48,
  alt,
}) {
  const [open, setOpen] = useState(false);
  const [broken, setBroken] = useState(false);
  const url = resolvePledgePhotoUrl(src);

  useEffect(() => {
    setBroken(false);
  }, [src, url]);

  if (!url || broken) return null;

  return (
    <>
      <Box
        component="button"
        type="button"
        onClick={() => setOpen(true)}
        title={`${label} — click to zoom`}
        sx={{
          all: 'unset',
          boxSizing: 'border-box',
          width: size,
          height: size,
          borderRadius: 1.5,
          overflow: 'hidden',
          cursor: 'zoom-in',
          border: '1px solid rgba(201,162,77,0.45)',
          boxShadow: '0 2px 8px rgba(15,23,42,0.1)',
          bgcolor: '#0b1220',
          display: 'inline-block',
          flexShrink: 0,
          verticalAlign: 'middle',
        }}
      >
        <Box
          component="img"
          src={url}
          alt={alt || label}
          sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          onError={() => setBroken(true)}
        />
      </Box>

      <Modal open={open} onClose={() => setOpen(false)} sx={{ display: 'grid', placeItems: 'center', p: 2 }}>
        <Box
          sx={{
            outline: 'none',
            width: 'min(92vw, 720px)',
            bgcolor: '#0b1220',
            borderRadius: 3,
            border: '1px solid rgba(212,175,106,0.35)',
            boxShadow: '0 24px 60px rgba(0,0,0,0.45)',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2,
              py: 1.25,
              borderBottom: '1px solid rgba(255,255,255,0.08)',
            }}
          >
            <Typography sx={{ color: '#f5e6c8', fontWeight: 750, fontSize: 15 }}>{label}</Typography>
            <IconButton
              onClick={() => setOpen(false)}
              aria-label="Close"
              size="small"
              sx={{
                bgcolor: '#fff',
                color: '#0f172a',
                '&:hover': { bgcolor: '#f1f5f9' },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Box
              component="img"
              src={url}
              alt={alt || label}
              sx={{ maxWidth: '100%', maxHeight: '72vh', objectFit: 'contain', borderRadius: 2 }}
            />
          </Box>
          <Box sx={{ px: 2, pb: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              onClick={() => setOpen(false)}
              variant="outlined"
              sx={{
                textTransform: 'none',
                borderRadius: 2,
                color: '#f5e6c8',
                borderColor: 'rgba(245,230,200,0.4)',
                '&:hover': { borderColor: '#f5e6c8', bgcolor: 'rgba(245,230,200,0.08)' },
              }}
            >
              Close
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
}

/** Side-by-side client + article thumbs with labels. */
export function PledgePhotosRow({ clientPhoto, articlePhoto, size = 56 }) {
  if (!clientPhoto && !articlePhoto) return null;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
      {clientPhoto ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.4 }}>
          <PhotoThumb src={clientPhoto} label="Client photo" size={size} />
          <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#64748b', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
            Client
          </Typography>
        </Box>
      ) : null}
      {articlePhoto ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.4 }}>
          <PhotoThumb src={articlePhoto} label="Article photo" size={size} />
          <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#64748b', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
            Article
          </Typography>
        </Box>
      ) : null}
    </Box>
  );
}
